<template>
    <div>
        <breadcrumbs />
        <page-title :text="$t('page_titles.communications_page_title')" />
        <v-tabs v-model="tab" class="mb-4" @update:model-value="onTabChange">
            <v-tab value="communications">{{ $t('communications') }}</v-tab>
            <v-tab value="notesLabel">{{ $t('notesLabel') }}</v-tab>
            <v-tab value="presetNotes">{{ $t('preset_notes') }}</v-tab>
            <v-tab value="calendar">{{ $t('calendar') }}</v-tab>
        </v-tabs>
        <v-tabs-window v-model="tab">
            <v-tabs-window-item value="communications">
                <communications
                    v-if="tab === 'communications'"
                    :communications="communications"
                    :communications-loading="communicationsLoading"
                    :communications-total-count="communicationsCount"
                    @get-communications="getCommunications"
                >
                    <template v-slot="{getCommunications}">
                        <communications-filters
                            v-model="communicationFilters"
                            :has-active-filters="hasActiveCommunicationFilters"
                            @submit="getCommunications"
                            @reset="resetCommunicationFilters(getCommunications)"
                        />
                    </template>
                </communications>
            </v-tabs-window-item>
            <v-tabs-window-item value="notesLabel">
                <internal-notes
                    :communications="internalNotes"
                    :communications-loading="internalNotesLoading"
                    :communications-total-count="internalNotesCount"
                    @get-communications="getInternalNotes"
                >
                    <template v-slot="{getCommunications}">
                        <internal-notes-filters
                            v-model="noteFilters"
                            :has-active-filters="hasActiveNoteFilters"
                            @submit="getCommunications"
                            @reset="resetNoteFilters(getCommunications)"
                        />
                    </template>
                </internal-notes>
            </v-tabs-window-item>
            <v-tabs-window-item value="presetNotes">
                <internal-notes
                    :communications="presetNotes"
                    :communications-loading="presetNotesLoading"
                    :communications-total-count="presetNotesCount"
                    preset
                    @get-communications="getPresetNotes"
                >
                    <template v-slot="{getCommunications}">
                        <internal-notes-filters
                            v-model="presetNoteFilters"
                            :has-active-filters="hasActivePresetNoteFilters"
                            preset
                            @submit="getCommunications"
                            @reset="resetPresetNoteFilters(getCommunications)"
                        />
                    </template>
                </internal-notes>
            </v-tabs-window-item>
            <v-tabs-window-item value="calendar">
                <communications
                    v-if="tab === 'calendar'"
                    is-calendar-mode
                    :communications="communications"
                    :communications-loading="communicationsLoading"
                    @get-communications="getCommunications"
                    @add-communication-filters="addCommunicationFilters"
                />
            </v-tabs-window-item>
        </v-tabs-window>
    </div>
</template>

<script lang="ts">
    import {Component, toNative, Vue} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import Communications from '@/components/account/communications/Communications.vue'
    import {clean, clone, isEqual} from '@/utils/helpers'
    import InternalNotes from '@/components/account/internalNotes/InternalNotes.vue'
    import CommunicationsFilters from '@/components/account/communications/CommunicationsFilters.vue'
    import InternalNotesFilters from '@/components/account/communications/InternalNotesFilters.vue'
    import PageTitle from '@/components/parts/PageTitle.vue'
    import {blankCommunicationFilters, blankNoteFilters} from '@/utils/communications-blank-states'

    @Component({
        components: {
            CommunicationsCalendar: defineAsyncComponent(
                () => import('@/components/account/communications/CommunicationsCalendar.vue')
            ),
            InternalNotesFilters,
            CommunicationsFilters,
            InternalNotes,
            Communications,
            Breadcrumbs,
            PageTitle,
        },
    })
    export class CommunicationsPage extends Vue {
        communicationsLoading = false
        communications = []
        communicationsCount = 0

        internalNotesLoading = false
        internalNotes = []
        internalNotesCount = 0

        presetNotesLoading = false
        presetNotes = []
        presetNotesCount = 0

        communicationFilters = blankCommunicationFilters()
        noteFilters = blankNoteFilters()
        presetNoteFilters = blankNoteFilters()

        tab = null

        async getCommunications({itemsPerPage, page, accountId}) {
            try {
                this.communicationsLoading = true
                this.communications = []
                const filters = clone(this.communicationFilters)
                clean(filters)
                if (filters.communicationDateFrom)
                    filters.communicationDateFrom = this.$dateFns.format(filters.communicationDateFrom) + 'T00:00'
                if (filters.createdDateFrom)
                    filters.createdDateFrom = this.$dateFns.format(filters.createdDateFrom) + 'T00:00'
                if (filters.modifiedDateFrom)
                    filters.modifiedDateFrom = this.$dateFns.format(filters.modifiedDateFrom) + 'T00:00'
                if (filters.communicationDateTo)
                    filters.communicationDateTo = this.$dateFns.format(filters.communicationDateTo) + 'T23:59'
                if (filters.createdDateTo)
                    filters.createdDateTo = this.$dateFns.format(filters.createdDateTo) + 'T23:59'
                if (filters.modifiedDateTo)
                    filters.modifiedDateTo = this.$dateFns.format(filters.modifiedDateTo) + 'T23:59'
                const {communications, count} = await this.$api.communications.get({
                    ...filters,
                    ...(accountId && {accountId}),
                    ...(itemsPerPage && {limit: itemsPerPage}),
                    ...(itemsPerPage && page && {offset: itemsPerPage * (page - 1)}),
                    typeNEQ: filters.typeNEQ || 'NOTES',
                })
                this.communications = communications
                this.communicationsCount = count
                this.communicationsLoading = false
            } catch (e) {
                this.communicationsCount = 0
                this.communications = []
            } finally {
                this.communicationsLoading = false
            }
        }

        onTabChange() {
            this.communicationFilters = blankCommunicationFilters()
        }

        async getInternalNotes({itemsPerPage, page}) {
            try {
                this.internalNotesLoading = true
                const filters = clone(this.noteFilters)
                clean(filters)
                if (filters.createdDateFrom) filters.createdDateFrom += 'T00:00'
                if (filters.modifiedDateFrom) filters.modifiedDateFrom += 'T00:00'
                if (filters.createdDateTo) filters.createdDateTo += 'T23:59'
                if (filters.modifiedDateTo) filters.modifiedDateTo += 'T23:59'
                const {communications, count} = await this.$api.communications.get({
                    ...filters,
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                    type: 'NOTES',
                    presetOnly: false,
                })
                this.internalNotes = communications
                this.internalNotesCount = count
                this.internalNotesLoading = false
            } catch (e) {
                this.internalNotesCount = 0
                this.internalNotes = []
            } finally {
                this.internalNotesLoading = false
            }
        }

        addCommunicationFilters(newFilters) {
            this.communicationFilters = {
                ...this.communicationFilters,
                ...newFilters,
            }
        }

        async getPresetNotes({itemsPerPage, page}) {
            try {
                this.presetNotesLoading = true
                const filters = clone(this.presetNoteFilters)
                filters.orderId = null
                clean(filters)
                if (filters.createdDateFrom) filters.createdDateFrom += 'T00:00'
                if (filters.modifiedDateFrom) filters.modifiedDateFrom += 'T00:00'
                if (filters.createdDateTo) filters.createdDateTo += 'T23:59'
                if (filters.modifiedDateTo) filters.modifiedDateTo += 'T23:59'
                const {communications, count} = await this.$api.communications.get({
                    ...filters,
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                    type: 'NOTES',
                    presetOnly: true,
                })
                this.presetNotes = communications
                this.presetNotesCount = count
                this.presetNotesLoading = false
            } catch (e) {
                this.presetNotesCount = 0
                this.presetNotes = []
            } finally {
                this.presetNotesLoading = false
            }
        }

        resetCommunicationFilters(getCommunications) {
            this.communicationFilters = blankCommunicationFilters()
            getCommunications()
        }

        resetNoteFilters(getCommunications) {
            this.noteFilters = blankNoteFilters()
            getCommunications()
        }

        resetPresetNoteFilters(getCommunications) {
            this.presetNoteFilters = blankNoteFilters()
            getCommunications()
        }

        get hasActiveCommunicationFilters() {
            return !isEqual(blankCommunicationFilters(), this.communicationFilters)
        }

        get hasActiveNoteFilters() {
            return !isEqual(blankNoteFilters(), this.noteFilters)
        }

        get hasActivePresetNoteFilters() {
            return !isEqual(blankNoteFilters(), this.presetNoteFilters)
        }
    }

    export default toNative(CommunicationsPage)
</script>
